import { useMemo } from 'react';
import type { HistoryGroup } from '../pages/AiChatPage/RightSidebar';
import type { AiConversationRecord } from '../types/aiHistory';
import type { AiDifyCapability } from '../types/aiDifyCapability';
import { formatTime } from '@/utils/timeUtils';

export interface UseHistoryGroupsParams {
  readonly dbConversations: readonly AiConversationRecord[];
  readonly capabilities: readonly AiDifyCapability[];
  readonly activeConversationDbId: number | null;
  readonly unreadConversationIds: ReadonlySet<number>;
  /** 正在执行流式生成的对话 DB ID 集合（支持多个同时执行） */
  readonly runningConversationDbIds?: ReadonlySet<number>;
  /** 外部传入的历史分组（优先使用） */
  readonly historyGroupsOverride?: readonly HistoryGroup[];
  /** 各会话最新一条消息的创建时间，用于排序（避免 date_updated 被 is_active 污染） */
  readonly lastMessageTimes?: Readonly<Record<number, string>>;
}

const EMPTY_GROUPS: readonly HistoryGroup[] = [
  { label: '今天', items: [{ id: 'empty', title: '暂无历史会话' }] }
];

interface HistoryItem {
  readonly id: string;
  readonly title: string;
  readonly subtitle: string;
  readonly isActive: boolean;
  readonly hasUnread: boolean;
  readonly isRunning: boolean;
}

export const useHistoryGroups = ({
  dbConversations,
  capabilities,
  activeConversationDbId,
  unreadConversationIds,
  runningConversationDbIds,
  historyGroupsOverride,
  lastMessageTimes
}: UseHistoryGroupsParams): readonly HistoryGroup[] => {
  return useMemo((): readonly HistoryGroup[] => {
    if (historyGroupsOverride && historyGroupsOverride.length > 0) {
      return historyGroupsOverride;
    }

    const getCapabilityName = (id: number | null | undefined): string => {
      if (id == null) return '未知助手';
      const found = capabilities.find(cap => cap.id === id);
      return found ? found.name : '未知助手';
    };

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;

    const buckets: { today: HistoryItem[]; yesterday: HistoryItem[]; earlier: HistoryItem[] } = {
      today: [],
      yesterday: [],
      earlier: []
    };

    // 按最新消息时间降序排序，没有消息的会话回退到创建时间
    const sortedConversations = [...dbConversations].sort((a, b) => {
      const tA = new Date(lastMessageTimes?.[a.id] ?? a.date_created).getTime();
      const tB = new Date(lastMessageTimes?.[b.id] ?? b.date_created).getTime();
      return tB - tA;
    });

    for (const c of sortedConversations) {
      // 优先用 lastMessageTimes（最新消息时间）分桶，否则回退到创建时间
      const time = new Date(lastMessageTimes?.[c.id] ?? c.date_created).getTime();
      const item: HistoryItem = {
        id: String(c.id),
        title: c.title ?? `对话 #${c.id}`,
        subtitle: `${getCapabilityName(c.dify_capability_id)} · ${formatTime(c.date_created)}`,
        isActive: activeConversationDbId === c.id,
        hasUnread: unreadConversationIds.has(c.id),
        isRunning: runningConversationDbIds?.has(c.id) ?? false
      };

      if (time >= todayStart) buckets.today.push(item);
      else if (time >= yesterdayStart) buckets.yesterday.push(item);
      else buckets.earlier.push(item);
    }

    const groups: HistoryGroup[] = [];
    if (buckets.today.length > 0) groups.push({ label: '今天', items: buckets.today });
    if (buckets.yesterday.length > 0) groups.push({ label: '昨天', items: buckets.yesterday });
    if (buckets.earlier.length > 0) groups.push({ label: '更早', items: buckets.earlier });

    return groups.length > 0 ? groups : EMPTY_GROUPS;
  }, [
    activeConversationDbId,
    dbConversations,
    historyGroupsOverride,
    capabilities,
    unreadConversationIds,
    runningConversationDbIds,
    lastMessageTimes
  ]);
};